import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import api, { authApi } from '../lib/api';

export default function LoginPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login, isAuthenticated } = useAuth();
    const { language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [processingCallback, setProcessingCallback] = useState(false);

    const t = {
        de: {
            subtitle: 'Exklusiv für Discord-Mitglieder',
            loginButton: 'Mit Discord anmelden',
            processing: 'Anmeldung wird verarbeitet...',
            requirements: 'Voraussetzungen:',
            memberReq: 'Mitglied auf dem Discord',
            roleReq: '[🔑│Guild Member] Rolle erforderlich',
            notMember: 'Noch kein Mitglied?',
            notMemberInfo: 'Trete dem Discord bei und erhalte die [🔑│Guild Member] Rolle, um Zugang zum Wiki zu bekommen.',
            errors: {
                not_on_server: 'Du musst Mitglied des Discord-Servers sein, um auf das Wiki zugreifen zu können.',
                no_verified_role: 'Du benötigst die "🔑│Guild Member"-Rolle auf dem Discord-Server, um auf das Wiki zugreifen zu können.',
                token_failed: 'Discord-Authentifizierung fehlgeschlagen. Bitte versuche es erneut.',
                user_info_failed: 'Benutzerinformationen konnten nicht abgerufen werden.',
                auth_failed: 'Authentifizierung fehlgeschlagen. Bitte versuche es erneut.',
                default: 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                session_failed: 'Session konnte nicht geladen werden. Bitte melde dich erneut an.',
                start_failed: 'Fehler beim Starten der Discord-Anmeldung.'
            }
        },
        en: {
            subtitle: 'Exclusive for Discord members',
            loginButton: 'Login with Discord',
            processing: 'Processing login...',
            requirements: 'Requirements:',
            memberReq: 'Member of the Discord server',
            roleReq: '[🔑│Guild Member] role required',
            notMember: 'Not a member yet?',
            notMemberInfo: 'Join the Discord and receive the [🔑│Guild Member] role to get access to the wiki.',
            errors: {
                not_on_server: 'You must be a member of the Discord server to access the wiki.',
                no_verified_role: 'You need the "🔑│Guild Member" role on the Discord server to access the wiki.',
                token_failed: 'Discord authentication failed. Please try again.',
                user_info_failed: 'Could not retrieve user information.',
                auth_failed: 'Authentication failed. Please try again.',
                default: 'An error occurred. Please try again.',
                session_failed: 'Session could not be loaded. Please log in again.',
                start_failed: 'Error starting Discord login.'
            }
        }
    };

    const texts = t[language] || t.en;

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
            return;
        }

        // Handle OAuth callback - check for session_id or error
        const sessionId = searchParams.get('session_id');
        const errorParam = searchParams.get('error');
        
        if (errorParam) {
            handleError(errorParam);
            // Clear URL params
            window.history.replaceState({}, document.title, '/login');
            return;
        }

        if (sessionId && !processingCallback) {
            handleSessionCallback(sessionId);
        }
    }, [isAuthenticated, navigate, searchParams, processingCallback]);

    const handleError = (errorCode) => {
        const errorTexts = texts.errors;
        switch (errorCode) {
            case 'not_on_server':
                setError(errorTexts.not_on_server);
                break;
            case 'no_verified_role':
                setError(errorTexts.no_verified_role);
                break;
            case 'token_failed':
                setError(errorTexts.token_failed);
                break;
            case 'user_info_failed':
                setError(errorTexts.user_info_failed);
                break;
            case 'auth_failed':
                setError(errorTexts.auth_failed);
                break;
            default:
                setError(errorTexts.default);
        }
    };

    const handleSessionCallback = async (sessionId) => {
        setProcessingCallback(true);
        setLoading(true);
        setError(null);
        
        try {
            // Fetch session data from backend
            const response = await authApi.getSession(sessionId);
            const sessionData = response.data;
            
            await login({
                session_id: sessionId,
                discord_id: sessionData.discord_id,
                username: sessionData.username,
                avatar: sessionData.avatar,
                roles: sessionData.roles,
                is_admin: sessionData.is_admin
            });
            
            // Clear URL params and navigate home
            window.history.replaceState({}, document.title, '/login');
            navigate('/');
        } catch (err) {
            console.error('Session callback error:', err);
            setError(texts.errors.session_failed);
            // Clear URL params
            window.history.replaceState({}, document.title, '/login');
        } finally {
            setLoading(false);
            setProcessingCallback(false);
        }
    };

    const handleDiscordLogin = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await api.get('/auth/discord/login');
            window.location.href = response.data.auth_url;
        } catch (err) {
            console.error('Login error:', err);
            setError(texts.errors.start_failed);
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
            {/* Background effects */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#5865F2]/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-xl">
                {/* Logo */}
                <div className="text-center mb-8 animate-fade-in">
                    <h1 className="text-4xl font-bold tracking-wider font-['Rajdhani']">
                        <span className="text-cyan-400">SystemHunter</span>{' '}
                        <span className="text-white">WIKI</span>
                    </h1>
                    <p className="text-gray-500 mt-2">
                        {texts.subtitle}
                    </p>
                </div>

                {/* Login Card */}
                <div className="glass glass-border rounded-2xl p-8 space-y-6 animate-fade-in stagger-2">
                    {/* Error Message */}
                    {error && (
                        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-3">
                            <XCircle className="text-red-400 shrink-0 mt-0.5" size={20} />
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Processing Callback */}
                    {processingCallback && (
                        <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30 flex items-center gap-3">
                            <div className="w-5 h-5 border-2 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin" />
                            <p className="text-cyan-400 text-sm">{texts.processing}</p>
                        </div>
                    )}

                    {/* Discord Login Button */}
                    <Button
                        data-testid="discord-login-button"
                        onClick={handleDiscordLogin}
                        disabled={loading || processingCallback}
                        className="w-full h-14 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold transition-all text-lg"
                    >
                        {loading && !processingCallback ? (
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                <svg className="w-7 h-7 mr-3" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                </svg>
                                {texts.loginButton}
                            </>
                        )}
                    </Button>

                    {/* Requirements Info */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                        <h3 className="text-sm font-semibold text-gray-400">{texts.requirements}</h3>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2 text-gray-500">
                                <CheckCircle2 size={16} className="text-green-400" />
                                {texts.memberReq}
                            </li>
                            <li className="flex items-center gap-2 text-gray-500">
                                <CheckCircle2 size={16} className="text-green-400" />
                                {texts.roleReq}
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Info Box with Discord Join Button */}
                <div className="mt-8 flex items-stretch gap-3 animate-fade-in stagger-3">
                    <div className="flex-1 p-4 rounded-lg border border-white/5 bg-white/2">
                        <div className="flex items-start gap-3">
                            <AlertCircle size={20} className="text-amber-400 shrink-0 mt-0.5" />
                            <div className="text-sm text-gray-400">
                                <p className="font-semibold text-amber-400 mb-1">{texts.notMember}</p>
                                <p>
                                    {texts.notMemberInfo}
                                </p>
                            </div>
                        </div>
                    </div>
                    <a
                        href="https://discord.gg/ba6V7HPDZy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 w-[100px] h-[100px] rounded-lg bg-[#5865F2] hover:bg-[#4752C4] flex items-center justify-center transition-colors"
                        title={language === 'de' ? 'Discord beitreten' : 'Join Discord'}
                    >
                        <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
}
